import React, { memo, useMemo, useState } from "react";
import styles from "./LoanProductSideDialog.module.scss";
import CommonSideDialog from "../../../../common/components/side-dialog/CommonSideDialog";
import CommonSelect, { CommonSelectItem } from "../../../../common/components/select/CommonSelect";
import { Controller, useForm } from "react-hook-form";
import { NumericFormat } from "react-number-format";
import { LoanProduct } from "../../../../model/solar-product/loan-product";
import solarProductService from "../../../../httpService/SolarProductService";
import { useDispatch } from "react-redux";
import { hideLoader, showLoader } from "../../../../stores/slices/loader-slice";
import { toast } from "react-toastify";
import CommonDialog from "../../../../common/components/dialog/CommonDialog";
import LoanProductOtpForm from "../otp-form/LoanProductOtpForm";

export type LoanProductDialogSolarProduct = {
	id: string;
	productName: string;
	cost: number;
	loanProduct?: LoanProduct;
};

type Props = {
	solarProduct: LoanProductDialogSolarProduct;
	onCloseDialog: () => void;
	onSuccessfulExecution: () => void;
};

type FormData = {
	percentageInterest: string;
	minMonths: CommonSelectItem | undefined;
	maxMonths: CommonSelectItem | undefined;
	rangeOfMonths: CommonSelectItem | undefined;
	percentageInterestIncrease: string;
	hotDeal: boolean;
	storeCostPrice: string;
};

const LoanOfferSideDialog: React.FC<Props> = memo((props: Props) => {
	const { solarProduct } = props;

	const [dialogOpen, setDialogOpen] = useState(false);

	const dispatch = useDispatch();

	const monthOptions = [
		new CommonSelectItem(3, "3 Months"),
		new CommonSelectItem(6, "6 Months"),
		new CommonSelectItem(9, "9 Months"),
		new CommonSelectItem(12, "12 Months"),
		new CommonSelectItem(15, "15 Months"),
		new CommonSelectItem(18, "18 Months"),
		new CommonSelectItem(21, "21 Months"),
		new CommonSelectItem(24, "24 Months"),
	];

	const rangeOfMonthsOptions = [
		new CommonSelectItem(1, "1 Month"),
		new CommonSelectItem(2, "2 Months"),
		new CommonSelectItem(3, "3 Months"),
		new CommonSelectItem(4, "4 Months"),
		new CommonSelectItem(5, "5 Months"),
		new CommonSelectItem(6, "6 Months"),
	];

	const {
		register,
		handleSubmit,
		watch,
		setValue,
		control,
		trigger,
		formState: { errors, isValid, isSubmitted },
	} = useForm<FormData>({
		defaultValues: {
			percentageInterest: solarProduct.loanProduct?.percentageInterestRate.toString(),
			minMonths: monthOptions.find(x => x.key === solarProduct.loanProduct?.minimumNumberOfMonths) || undefined,
			maxMonths: monthOptions.find(x => x.key === solarProduct.loanProduct?.maximumNumberOfMonths) || undefined,
			rangeOfMonths: rangeOfMonthsOptions.find(x => x.key === solarProduct.loanProduct?.percentageIncreaseMonthStep) || undefined,
			percentageInterestIncrease: solarProduct.loanProduct?.percentageIncrease.toString(),
			hotDeal: solarProduct?.loanProduct?.hotDeal,
			storeCostPrice: solarProduct?.loanProduct?.storeCostPrice?.toString(),
		},
		reValidateMode: "onChange",
		mode: "onTouched",
	});

	const minMonthsWatch = watch("minMonths");
	const percentageInterestWatch = watch("percentageInterest");

	const finalCost = useMemo(() => {
		const percentageInterest = Number.parseFloat(percentageInterestWatch || "0");
		const minMonths = minMonthsWatch?.key || 0;

		return solarProduct.cost + ((0.7 * solarProduct.cost * percentageInterest) / 100) * minMonths;
	}, [percentageInterestWatch, minMonthsWatch, solarProduct.cost]);

	const handleAddNewLoanOffer = () => {
		handleSubmit(data => {
			dispatch(showLoader());
			solarProductService
				.addNewLoanProduct(
					solarProduct.id,
					new LoanProduct({
						maximumNumberOfMonths: data.maxMonths?.key,
						minimumNumberOfMonths: data.minMonths?.key,
						percentageIncrease: Number.parseFloat(data.percentageInterestIncrease),
						percentageIncreaseMonthStep: data.rangeOfMonths?.key,
						percentageInterestRate: Number.parseFloat(data.percentageInterest),
						hotDeal: data.hotDeal,
						storeCostPrice: Number.parseFloat(data.storeCostPrice),
					})
				)
				.then(() => {
					props.onCloseDialog();
					props.onSuccessfulExecution();
					toast.success("Loan product added successfully");
				}).catch(error => { console.log('error', error)
					toast.error(error.response.data.title || "Something went wrong. Please try again.");
				})
				.finally(() => {
					dispatch(hideLoader());
				});
		})();
	};

	const handleEditLoanOffer = async () => {
		handleSubmit(data => {
			dispatch(showLoader());
			solarProductService
				.editLoanProduct(
					solarProduct.id,
					new LoanProduct({
						maximumNumberOfMonths: data.maxMonths?.key,
						minimumNumberOfMonths: data.minMonths?.key,
						percentageIncrease: Number.parseFloat(data.percentageInterestIncrease),
						percentageIncreaseMonthStep: data.rangeOfMonths?.key,
						percentageInterestRate: Number.parseFloat(data.percentageInterest),
						hotDeal: data.hotDeal,
						storeCostPrice: Number.parseFloat(data.storeCostPrice),
					})
				)
				.then(result => {
					if (result.data.otpSent) {
						toast.success("OTP sent successfully");
						openDialog();
					}
				})
				.catch(error => { console.log('error', error)
					toast.error(error.response.data.title || "Something went wrong. Please try again.");
				}).finally(() => {
					dispatch(hideLoader());
				});
		})();
	};

	const handleVerifyOtp = async (otp: string) => {
		handleSubmit(data => {
			dispatch(showLoader());
			solarProductService
				.editLoanProduct(
					solarProduct.id,
					new LoanProduct({
						maximumNumberOfMonths: data.maxMonths?.key,
						minimumNumberOfMonths: data.minMonths?.key,
						percentageIncrease: Number.parseFloat(data.percentageInterestIncrease),
						percentageIncreaseMonthStep: data.rangeOfMonths?.key,
						percentageInterestRate: Number.parseFloat(data.percentageInterest),
						hotDeal: data.hotDeal,
						storeCostPrice: Number.parseFloat(data.storeCostPrice),
					}),
					otp
				)
				.then(result => {
					props.onCloseDialog();
					props.onSuccessfulExecution();
				})
				.finally(() => {
					dispatch(hideLoader());
				})
				.catch(error => {
					toast.error(error.response.data.title || "Something went wrong. Please try again.");
				});
		})();
	};

	const handleResendOtp = async () => {
		handleSubmit(data => {
			solarProductService
				.editLoanProduct(
					solarProduct.id,
					new LoanProduct({
						maximumNumberOfMonths: data.maxMonths?.key,
						minimumNumberOfMonths: data.minMonths?.key,
						percentageIncrease: Number.parseFloat(data.percentageInterestIncrease),
						percentageIncreaseMonthStep: data.rangeOfMonths?.key,
						percentageInterestRate: Number.parseFloat(data.percentageInterest),
						hotDeal: data.hotDeal,
						storeCostPrice: Number.parseFloat(data.storeCostPrice),
					})
				)
				.then(result => {
					if (result.data.otpSent) {
						toast.success("OTP sent successfully");
					}
				})
				.catch(error => {
					toast.error(error.response.data.title || "Something went wrong. Please try again.");
				});
		})();
	};

	const toggleDialog = () => {
		setDialogOpen(!dialogOpen);
	};

	const openDialog = () => {
		setDialogOpen(true);
	};

	return (
		<>
			<CommonSideDialog onCloseDialog={props.onCloseDialog}>
				<div className={styles.titleContainer}>
					<button className={`standardButton ${styles.backButton}`} onClick={props.onCloseDialog}>
						<svg width="14" height="13" viewBox="0 0 14 13" fill="none" xmlns="http://www.w3.org/2000/svg">
							<path
								d="M3.52214 7.33342H12.8346C13.0707 7.33342 13.2687 7.25356 13.4284 7.09384C13.5881 6.93412 13.668 6.7362 13.668 6.50009C13.668 6.26398 13.5881 6.06606 13.4284 5.90634C13.2687 5.74662 13.0707 5.66676 12.8346 5.66676H3.52214L7.60547 1.58342C7.77214 1.41676 7.852 1.22231 7.84505 1.00009C7.83811 0.777869 7.7513 0.583424 7.58464 0.416758C7.41797 0.26398 7.22352 0.184119 7.0013 0.177174C6.77908 0.17023 6.58464 0.250091 6.41797 0.416758L0.917969 5.91676C0.834636 6.00009 0.775608 6.09037 0.740886 6.18759C0.706163 6.28481 0.688803 6.38898 0.688803 6.50009C0.688803 6.6112 0.706163 6.71537 0.740886 6.81259C0.775608 6.90981 0.834636 7.00009 0.917969 7.08342L6.41797 12.5834C6.57075 12.7362 6.76172 12.8126 6.99089 12.8126C7.22005 12.8126 7.41797 12.7362 7.58464 12.5834C7.7513 12.4168 7.83464 12.2188 7.83464 11.9897C7.83464 11.7605 7.7513 11.5626 7.58464 11.3959L3.52214 7.33342Z"
								fill="#1C1B1F"
							/>
						</svg>
					</button>
					<div className={styles.title}>{solarProduct.loanProduct ? "Edit Loan Offer" : "Add New Loan Offer"}</div>
					<button
						className={`standardButton ${styles.createOfferButton}`}
						disabled={isSubmitted && !isValid}
						onClick={solarProduct.loanProduct ? handleEditLoanOffer : handleAddNewLoanOffer}
					>
						{solarProduct.loanProduct ? "Save Changes" : "Create Offer"}
					</button>
				</div>
				<table className={styles.contentContainer}>
					<tbody>
						<tr className={styles.formItem}>
							<td className={styles.formItemName}>Product Name</td>
							<td className={styles.infoItem}>{solarProduct.productName}</td>
						</tr>
						<tr className={styles.formItem}>
							<td className={styles.formItemName}>Product Amount</td>
							<td className={styles.infoItem}>{`₦ ${solarProduct.cost.toLocaleString()}`}</td>
						</tr>
						<tr className={styles.formItem}>
							<td className={styles.formItemName}>Enter percentage interest</td>
							<td className={styles.formItemInput}>
								<Controller
									render={({ field }) => (
										<NumericFormat
											placeholder="Enter Percentage Interest"
											suffix="%"
											allowNegative={false}
											allowLeadingZeros={false}
											maxLength={4}
											max={100}
											min={0}
											onValueChange={values => {
												if (Number.parseFloat(values.value) > 100) {
													values.value = "100";
												}
												field.onChange(values.value);
											}}
											onBlur={field.onBlur}
											value={field.value}
										/>
									)}
									name="percentageInterest"
									control={control}
									rules={{
										required: "Percentage interest is required",
										validate: value => {
											if (Number.parseFloat(value) <= 0) {
												return "Percentage interest cannot be less than or equal to 0";
											}
											if (Number.parseFloat(value) > 100) {
												return "Percentage interest cannot be greater than 100";
											}
											return true;
										},
									}}
								/>
								<div className={styles.errors}>{errors.percentageInterest?.message}</div>
							</td>
						</tr>
						<tr className={styles.formItem}>
							<td className={styles.formItemName}>Minimum number of months</td>
							<td>
								<CommonSelect
									selected={watch("minMonths")}
									items={monthOptions}
									searchable
									registerProp={register("minMonths", {
										required: "Minimum number of months is required",
										validate: value => {
											if (value?.key >= watch("maxMonths")?.key) {
												return "Minimum number of months cannot be greater than or equal to the maximum number of months";
											}
											return true;
										},
									})}
									placeholder="Select Minimum Number of Months"
									onChange={item => {
										setValue("minMonths", item, {
											shouldDirty: true,
											shouldValidate: true,
											shouldTouch: true,
										});
										trigger("maxMonths");
									}}
								/>
								{isSubmitted && <div className={styles.errors}>{errors.minMonths?.message}</div>}
							</td>
						</tr>
						<tr className={styles.formItem}>
							<td className={styles.formItemName}>Maximum number of months</td>
							<td>
								<CommonSelect
									selected={watch("maxMonths")}
									items={monthOptions}
									searchable
									registerProp={register("maxMonths", {
										required: "Maximum number of months is required",
										validate: value => {
											if (value?.key <= watch("minMonths")?.key) {
												return "Maximum number of months cannot be less than or equal to the minimum number of months";
											}
											return true;
										},
									})}
									placeholder="Select Maximum Number of Months"
									onChange={item => {
										setValue("maxMonths", item, {
											shouldDirty: true,
											shouldValidate: true,
											shouldTouch: true,
										});
										trigger("minMonths");
									}}
								/>
								{isSubmitted && <div className={styles.errors}>{errors.maxMonths?.message}</div>}
							</td>
						</tr>
						<tr className={styles.formItem}>
							<td className={styles.formItemName}>Range of months</td>
							<td>
								<CommonSelect
									selected={watch("rangeOfMonths")}
									items={rangeOfMonthsOptions}
									searchable
									registerProp={register("rangeOfMonths", { required: "Range of months is required" })}
									placeholder="Select Range of Months"
									onChange={item => {
										setValue("rangeOfMonths", item, {
											shouldDirty: true,
											shouldValidate: true,
											shouldTouch: true,
										});
									}}
								/>
								<div className={styles.errors}>{errors.rangeOfMonths?.message}</div>
							</td>
						</tr>
						<tr className={styles.formItem}>
							<td className={styles.formItemName}>Percentage interest</td>
							<td className={styles.formItemInput}>
								<Controller
									render={({ field }) => (
										<NumericFormat
											placeholder="Enter Percentage Interest Increase"
											suffix="%"
											allowNegative={false}
											allowLeadingZeros={false}
											maxLength={4}
											max={100}
											min={0}
											onValueChange={values => {
												if (Number.parseFloat(values.value) > 100) {
													values.value = "100";
												}
												field.onChange(values.value);
											}}
											onBlur={field.onBlur}
											value={field.value}
										/>
									)}
									name="percentageInterestIncrease"
									control={control}
									rules={{
										required: "Percentage interest is required",
										validate: value => {
											if (Number.parseFloat(value) <= 0) {
												return "Percentage interest cannot be less than or equal to 0";
											}
											if (Number.parseFloat(value) > 100) {
												return "Percentage interest cannot be greater than 100";
											}
											return true;
										},
									}}
								/>
								<div className={styles.errors}>{errors.percentageInterestIncrease?.message}</div>
							</td>
						</tr>

						<tr className={styles.formItem}>
							<td className={styles.formItemName}>Final Cost</td>
							<td className={styles.formItemInput}>
								<input readOnly type="text" value={`₦ ${finalCost.toLocaleString()}`} />
							</td>
						</tr>
						<tr className={styles.formItem}>
							<td className={styles.formItemName}>Store Cost Price</td>
							<td className={styles.formItemInput}>
								<Controller
									render={({ field }) => (
										<NumericFormat
											placeholder="Input Store Cost Price"
											prefix="₦ "
											allowNegative={false}
											allowLeadingZeros={false}
											min={0}
											onBlur={field.onBlur}
											value={field.value}
											onValueChange={(values) => {
												field.onChange(values.value);
											}}
										/>
									)}
									name="storeCostPrice"
									control={control}
									rules={{
										required: "Store Cost Price is required"
									}}
								/>
								<div className={styles.errors}>{errors.storeCostPrice?.message}</div>
							</td>
						</tr>
						<tr className={styles.formItem}>
							<td className={styles.formItemName}>Hot Deal</td>
							<td style={{ marginLeft: 10}}>
								<input required={false} type='checkbox' {...register('hotDeal')} />
								<div className={styles.errors}>{errors.hotDeal?.message}</div>
								</td>
						</tr>
					</tbody>
				</table>
			</CommonSideDialog>
			{dialogOpen && (
				<CommonDialog>
					<LoanProductOtpForm onResendOtp={handleResendOtp} onVerifyOtp={handleVerifyOtp} onCloseDialog={toggleDialog} />
				</CommonDialog>
			)}
		</>
	);
});

export default LoanOfferSideDialog;
